import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useAssignRequest, useReviewers } from "@/hooks/useRequests";
import type { BlinkRequest } from "@/lib/types";

interface AssignDialogProps {
  request: BlinkRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Hand a request to any reviewer (Pod Reviewer, PM or Admin). */
export function AssignDialog({
  request,
  open,
  onOpenChange,
}: AssignDialogProps) {
  const { toast } = useToast();
  const { data: reviewers = [], isLoading } = useReviewers(open);
  const { mutate: assign, isPending } = useAssignRequest(request.id);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (open) setEmail("");
  }, [open]);

  const current = request.claimed_by_email?.toLowerCase();

  const handleAssign = () => {
    if (!email) return;
    assign(email, {
      onSuccess: (res) => {
        toast({
          title: `Assigned to ${res.claimed_by}`,
          description: "They've been notified by email.",
        });
        onOpenChange(false);
      },
      onError: (err) =>
        toast({
          title: "Could not assign the request",
          description: err.message,
          variant: "destructive",
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign request</DialogTitle>
          <DialogDescription>
            The person you choose becomes the owner of this request.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 py-2">
          <Label htmlFor="assign-to">Assign to</Label>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Select value={email} onValueChange={setEmail}>
              <SelectTrigger id="assign-to">
                <SelectValue placeholder="Choose a reviewer" />
              </SelectTrigger>
              <SelectContent>
                {reviewers.map((r) => (
                  <SelectItem
                    key={r.email}
                    value={r.email}
                    disabled={r.email.toLowerCase() === current}
                  >
                    {r.display_name}
                    {r.email.toLowerCase() === current
                      ? " (current owner)"
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!email || isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
